// /anomaliler — Karantina Sayfası (CR2-09).
// Açık anomaliler (App-level openAnomalies'tan) + "Manuel düzelttim" checkbox
// (POST /anomalies/:id/resolve). Geçmiş 14 burada API'den çekilir.
//
// Resolve akışı: checkbox işaretle → handleResolve(id) → App optimistic remove
// + POST. Hata olursa App state geri alır, throw'u burada yakalayıp UI uyarısı.

import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  getAnomaliesHistory,
  ApiError,
  type ApiConfig,
} from "../lib/api";
import type { Anomaly } from "../types/api";
import AnomalyBadge from "../components/AnomalyBadge";

interface Props {
  cfg: ApiConfig;
  openAnomalies: Anomaly[];
  openAnomaliesCount: number;
  loading: boolean;
  error: string | null;
  onResolve: (id: string) => Promise<void>;
  onReload: () => Promise<void>;
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / (1000 * 60));
  if (min < 1) return "az önce";
  if (min < 60) return `${min} dk önce`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} saat önce`;
  const d = Math.floor(h / 24);
  if (d === 1) return "dün";
  if (d < 7) return `${d} gün önce`;
  return new Date(iso).toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "short",
  });
}

function shortDate(iso?: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "short",
  });
}

function AnomalyRow({
  a,
  isPending,
  onToggle,
}: {
  a: Anomaly;
  isPending: boolean;
  onToggle: (id: string, checked: boolean) => void;
}) {
  return (
    <li
      data-done={isPending}
      className={`anomali-row rounded-xl bg-ink-800 border border-ink-700 p-3 ${
        isPending ? "is-done" : ""
      }`}
    >
      <article>
        <div className="flex items-start gap-2">
          <span
            aria-hidden="true"
            className="inline-flex items-center justify-center w-6 h-6 rounded bg-warn-500/15 text-warn-400 shrink-0 mt-0.5"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01" />
            </svg>
          </span>
          <div className="grow min-w-0">
            <p className="hedef-dili text-ink-50 text-[15px] leading-snug">
              {a.message}
            </p>
            <p className="text-[11px] text-ink-400 mt-1.5">
              <span className="fon-dili text-ink-200">{a.fund}</span>
              <span className="mx-1.5 text-ink-600">·</span>
              {timeAgo(a.triggered_at)}
              {a.impact && (
                <>
                  <span className="mx-1.5 text-ink-600">·</span>
                  {a.impact}
                </>
              )}
            </p>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2 pl-8">
          <label className="inline-flex items-center gap-2 text-[12px] text-ink-200 cursor-pointer select-none">
            <input
              type="checkbox"
              className="anomali-checkbox w-4 h-4 rounded border-ink-500 bg-ink-700 text-accent-500 focus:ring-accent-500 focus:ring-offset-0"
              checked={isPending}
              disabled={isPending}
              onChange={(e) => onToggle(a.id, e.target.checked)}
            />
            <span>Manuel düzelttim</span>
          </label>

          <details className="ml-auto">
            <summary className="text-[11px] text-ink-400 hover:text-ink-200 cursor-pointer list-none">
              <span className="caret">▸</span> Payload
            </summary>
            <pre className="mt-2 text-[10px] fon-dili bg-ink-900 border border-ink-700 rounded p-2 text-ink-300 overflow-x-auto no-scrollbar">
              {JSON.stringify(a.payload, null, 2)}
            </pre>
          </details>
        </div>
      </article>
    </li>
  );
}

export default function Anomaliler({
  cfg,
  openAnomalies,
  openAnomaliesCount,
  loading,
  error,
  onResolve,
  onReload,
}: Props) {
  const navigate = useNavigate();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<Anomaly[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);
  // Optimistic UI sırasında "resolve in-flight" id'leri — checkbox tekrar tıklanmasın.
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [pendingIds, setPendingIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!cfg.base) {
      setHistoryLoading(false);
      return;
    }
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError(null);
    getAnomaliesHistory(cfg, 14)
      .then((out) => {
        if (!cancelled) setHistory(out.items);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setHistoryError(
          e instanceof ApiError
            ? `${e.message} (HTTP ${e.status})`
            : e instanceof Error
              ? e.message
              : "Beklenmedik hata",
        );
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cfg]);

  async function handleToggle(id: string, checked: boolean) {
    if (!checked) return; // resolve geri alınamaz; sadece check = resolve
    setPendingIds((prev) => new Set(prev).add(id));
    setResolveError(null);
    try {
      await onResolve(id);
    } catch (e) {
      setResolveError(
        e instanceof ApiError
          ? `${e.message} (HTTP ${e.status})`
          : e instanceof Error
            ? e.message
            : "Anomali çözülemedi",
      );
    } finally {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  const acikCount = openAnomalies.length;
  const allClean = !loading && acikCount === 0;

  return (
    <main
      className="min-h-screen flex flex-col mx-auto"
      style={{ maxWidth: 430 }}
    >
      {/* Header */}
      <header className="sticky top-0 z-20 bg-ink-900/95 backdrop-blur border-b border-ink-700/60">
        <div className="flex items-center justify-between px-4 pt-4 pb-3">
          <div className="flex items-baseline gap-2 min-w-0">
            <button
              onClick={() => navigate("/")}
              className="text-ink-100 font-semibold tracking-tight text-base hover:text-accent-300 transition"
            >
              AiFon
            </button>
            <span className="text-ink-400 text-sm">·</span>
            <span className="text-ink-300 text-sm">Anomaliler</span>
          </div>
          <div className="flex items-center gap-1">
            <AnomalyBadge
              count={openAnomaliesCount}
              onClick={() => navigate("/anomaliler")}
            />
          </div>
        </div>
      </header>

      <section className="px-4 pt-5">
        <div className="flex items-start gap-3">
          <button
            onClick={() => navigate("/")}
            aria-label="Geri"
            className="text-ink-300 hover:text-ink-100 -ml-1 p-1"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="grow min-w-0">
            <h2 className="hedef-dili text-ink-50 text-xl leading-snug">
              Anomaliler
            </h2>
            <p className="text-ink-300 text-sm mt-0.5">
              <span
                className={
                  allClean
                    ? "text-gain-400 font-semibold"
                    : "text-warn-400 font-semibold"
                }
              >
                {acikCount}
              </span>{" "}
              açık · son 30 gün
            </p>
          </div>
          <button
            type="button"
            onClick={() => void onReload()}
            className="text-[11px] text-ink-400 hover:text-ink-200 transition"
            title="Tekrar yükle"
          >
            ↻
          </button>
        </div>
      </section>

      {error && (
        <section className="px-4 mt-3">
          <div className="rounded-lg bg-loss-500/10 border border-loss-500/30 px-3 py-2 text-[12px] text-loss-400">
            Veri çekilemedi: {error}
            {!cfg.base && (
              <span className="block mt-1 text-ink-300">
                Settings'ten API base'i ayarla.
              </span>
            )}
          </div>
        </section>
      )}

      {resolveError && (
        <section className="px-4 mt-3">
          <div className="rounded-lg bg-loss-500/10 border border-loss-500/30 px-3 py-2 text-[12px] text-loss-400">
            {resolveError}
          </div>
        </section>
      )}

      {/* Disiplin satırı */}
      <section className="px-4 mt-4">
        <div
          className={`rounded-lg px-3 py-2 border transition-colors duration-300 ${
            allClean
              ? "bg-gain-500/10 border-gain-500/30"
              : "bg-warn-500/10 border-warn-500/30"
          }`}
        >
          <p
            className={`text-[12px] leading-snug ${
              allClean ? "text-gain-400" : "text-warn-400"
            }`}
          >
            {loading
              ? "Yükleniyor…"
              : allClean
                ? "Tüm anomaliler temiz — anne sahnesine hazırsınız."
                : `${acikCount} açık anomali var — anne sahnesine hazır değilsiniz.`}
          </p>
        </div>
      </section>

      {/* Açık anomaliler */}
      <section className="px-4 mt-4">
        <h3 className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold mb-2">
          Açık
        </h3>
        {loading ? (
          <p className="text-[11px] text-ink-400 italic px-1 py-2">Yükleniyor…</p>
        ) : openAnomalies.length === 0 ? (
          <p className="text-[11px] text-ink-400 italic px-1 py-2">
            Açık anomali yok.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {openAnomalies.map((a) => (
              <AnomalyRow
                key={a.id}
                a={a}
                isPending={pendingIds.has(a.id)}
                onToggle={handleToggle}
              />
            ))}
          </ul>
        )}
      </section>

      {/* Geçmiş 14 */}
      <section className="px-4 mt-6">
        <button
          type="button"
          onClick={() => setHistoryOpen((v) => !v)}
          className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold flex items-center gap-2 hover:text-ink-200"
          aria-expanded={historyOpen}
        >
          <span
            className="caret inline-block transition-transform"
            style={{ transform: historyOpen ? "rotate(90deg)" : "rotate(0deg)" }}
          >
            ▸
          </span>{" "}
          Geçmiş {historyLoading ? "…" : history.length} · son 30 gün
        </button>
        {historyOpen &&
          (historyLoading ? (
            <p className="text-[11px] text-ink-500 italic px-1 py-2 mt-3">
              Yükleniyor…
            </p>
          ) : historyError ? (
            <p className="text-[11px] text-loss-400 px-1 py-2 mt-3">
              {historyError}
            </p>
          ) : history.length === 0 ? (
            <p className="text-[11px] text-ink-500 italic px-1 py-2 mt-3">
              Geçmiş anomali yok.
            </p>
          ) : (
            <ul className="flex flex-col gap-1.5 mt-3 opacity-60">
              {history.map((h) => (
                <li
                  key={h.id}
                  className="text-[12px] text-ink-300 flex items-center gap-2 px-3 py-1.5 rounded bg-ink-800/60 border border-ink-700/40"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#4ade80"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span className="hedef-dili">
                    {h.short_label ?? h.message}
                  </span>
                  <span className="ml-auto fon-dili text-ink-500 text-[10px]">
                    {shortDate(h.resolved_at ?? h.triggered_at)}
                  </span>
                </li>
              ))}
            </ul>
          ))}
      </section>

      {/* Tüm anomaliler temiz mesajı */}
      {allClean && (
        <section className="px-4 mt-6">
          <div className="rounded-xl bg-gain-500/10 border border-gain-500/30 p-4 text-center">
            <svg
              className="mx-auto mb-2"
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#4ade80"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <p className="hedef-dili text-gain-400 text-base">
              Tüm anomaliler temiz.
            </p>
            <p className="text-[12px] text-ink-300 mt-1">
              Anne sahnesine hazırsınız.
            </p>
          </div>
        </section>
      )}

      <footer className="mt-auto px-4 pb-6 pt-8 text-center text-[11px] text-ink-400">
        AiFon · Anomali · Karantina
      </footer>
    </main>
  );
}
